import {
  Button,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
} from '@airbus/components-react';
import { produce } from 'immer';
import { useReducer } from 'react';

export const Status = {
  Initial: 'initial',
  FromDateSet: 'fromDateSet',
  Done: 'done',
} as const;

export type Status = (typeof Status)[keyof typeof Status];

const Messages = {
  [Status.Initial]: 'Please select the duration of the records to export.',
  [Status.FromDateSet]: 'Please select the end date of the records to export.',
  [Status.Done]: 'Please click "Download Report" to fetch the records.',
};

export const Events = {
  FROM_DATE_SET: 'FROM_DATE_SET',
  DATES_SET: 'DATES_SET',
  DATES_REMOVED: 'DATES_REMOVED',
} as const;

export type Events = (typeof Events)[keyof typeof Events];

export type DurationSelectorStateType = {
  status: Status;
  fromDate: Date;
  toDate: Date;
};

const initialState: DurationSelectorStateType = {
  status: Status.Initial,
  fromDate: null,
  toDate: null,
};

export function durationReducer(state = initialState, event) {
  switch (state.status) {
    case Status.Initial:
      if (event.type === Events.FROM_DATE_SET) {
        return produce(state, (draftState: DurationSelectorStateType) => {
          draftState.status = Status.FromDateSet;
          draftState.fromDate = event.data[0];
          draftState.toDate = null;
        });
      } else if (event.type === Events.DATES_SET) {
        return produce(state, (draftState: DurationSelectorStateType) => {
          draftState.status = Status.Done;
          draftState.fromDate = event.data[0];
          draftState.toDate = event.data[1];
        });
      }
      return state;
    case Status.FromDateSet:
      if (event.type === Events.DATES_SET) {
        return produce(state, (draftState: DurationSelectorStateType) => {
          draftState.status = Status.Done;
          draftState.fromDate = event.data[0];
          draftState.toDate = event.data[1];
        });
      } else if (event.type === Events.DATES_REMOVED) {
        return produce(state, (draftState: DurationSelectorStateType) => {
          draftState.status = Status.Initial;
          draftState.fromDate = null;
          draftState.toDate = null;
        });
      }
      return state;
    case Status.Done:
      if (event.type === Events.DATES_REMOVED) {
        return produce(state, (draftState: DurationSelectorStateType) => {
          draftState.status = Status.Initial;
          draftState.fromDate = null;
          draftState.toDate = null;
        });
      } else if (event.type === Events.FROM_DATE_SET) {
        return produce(state, (draftState: DurationSelectorStateType) => {
          draftState.status = Status.FromDateSet;
          draftState.fromDate = event.data[0];
          draftState.toDate = null;
        });
      }
      return state;
    default:
      return state;
  }
}

export default function DurationSelectorDialog({ open, onClose, onSelect }) {
  const [state, dispatch] = useReducer(durationReducer, initialState);

  const handleDateChange = (dates) => {
    if (!dates) {
      dispatch({ type: Events.DATES_REMOVED });
    } else if (dates.length === 2) {
      dispatch({ type: Events.DATES_SET, data: dates });
    } else if (dates.length === 1) {
      dispatch({ type: Events.FROM_DATE_SET, data: dates });
    }
  };

  const handleOK = () => {
    onClose();
    onSelect({ from: state.fromDate, to: state.toDate });
  };

  const getMessage = () => {
    return Messages[state.status] || '';
  };

  return (
    <Dialog open={open} onClose={() => onClose()}>
      <DialogHeader>Select Date Range</DialogHeader>
      <DialogContent>
        <p>{getMessage()}</p>
        <DatePicker range onChange={handleDateChange} />
      </DialogContent>
      <DialogActions>
        <Button variant='secondary' onClick={() => onClose()}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleOK} disabled={state.status !== Status.Done}>
          Download Report
        </Button>
      </DialogActions>
    </Dialog>
  );
}
